import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import { observer, inject } from 'mobx-react'
import { toJS, extendObservable, computed } from 'mobx'
import PropTypes from 'prop-types'
import map from 'lodash/map'
import union from 'lodash/union'
import ExpertSearchItem from './ExpertSearchItem'
import Checkbox from '@components/Checkbox'
import Add from '@components/Icon/Add'
import InfoIcon from '@components/Icon/Info'
import ChevronIcon from '@components/icons/ChevronIcon'
import { Dropdown } from '@components/Autocomplete'
import Tooltip from '@components/Tooltip'
import Spinner from '@components/spinner'
import CircularProgress from '@material-ui/core/CircularProgress'
import LinearProgress from '@material-ui/core/LinearProgress'
import { TextField, Button } from '@material-ui/core'
import Autocomplete, {
  createFilterOptions
} from '@material-ui/lab/Autocomplete'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { ExpertFieldOptions } from '../components/ExpertBulkCreate/helpers/expertFieldOptions'
import {
  AddToProcessingActionEnum,
  AddToProcessingExpertTypeEnum,
  InitializeProcessingActionEnum,
  sendAddToProcessingMetric,
  sendInitializeProcessingMetric
} from '../components/ExpertBulkCreate/helpers/expertBulkCreate.metrics'
import { v4 as uuidv4 } from 'uuid'
import { sendMetric } from '@atheneum/api/dist/lib'

const SHERLOCK_LIMIT = 25

class ExpertSearchResults extends Component {
  static propTypes = {
    expertSearchStore: PropTypes.object,
    history: PropTypes.object,
    growlStore: PropTypes.object,
    modalStore: PropTypes.object,
    expertBulkCreateStore: PropTypes.object
  }

  constructor(props) {
    super(props)

    this.state = {
      bulkCreateTrackingIds: [],
      bulkCreateCompletedExperts: [],
      bulkCreateFailedExperts: []
    }

    this.addToProjectFormOpened = false
    this.bulkAddJobStartTime = 0
    this.bulkExpertCreateIdentifier = `ADD_TO_PROJECT_${uuidv4()}`

    extendObservable(this, {
      areAllPinnedSelected: computed(
        () =>
          !this.props.expertSearchStore.pinnedExperts
            .filter(e => e.data.experience?.company)
            .find((e, i) => i < SHERLOCK_LIMIT && !e.checked)
      ),
      areAllExpertsSelected: computed(
        () =>
          !this.props.expertSearchStore.experts
            .filter(e => e.experience?.company)
            .find((e, i) => i < SHERLOCK_LIMIT && !e.checked)
      )
    })

    props.expertSearchStore.addHistory(this.props.history)
    this.checkAll(false)

    this.processingExpertsDispose = props.expertBulkCreateStore.observeProcessingExperts(
      experts => {
        const expertsToProcess = experts.filter(
          e => e.customIdentifier === this.bulkExpertCreateIdentifier
        )
        if (expertsToProcess.length > 0) {
          this.setState((prevState, props) => {
            return {
              bulkCreateTrackingIds: [
                ...prevState.bulkCreateTrackingIds,
                ...expertsToProcess.map(e => e.bulkCreateTrackingId)
              ]
            }
          })
        }
      }
    )

    this.completedExpertsDispose = props.expertBulkCreateStore.observeCompletedExperts(
      experts => {
        const expertsToProcess = experts.filter(
          e => e.customIdentifier === this.bulkExpertCreateIdentifier
        )
        if (expertsToProcess.length > 0) {
          this.setState((prevState, props) => {
            return {
              bulkCreateCompletedExperts: [
                ...prevState.bulkCreateCompletedExperts,
                ...expertsToProcess
              ]
            }
          }, this.tryOpenExpertToProjectForm)
        }
      }
    )

    this.errorExpertsDispose = props.expertBulkCreateStore.observeErrorExperts(
      experts => {
        const expertsToProcess = experts.filter(
          e => e.customIdentifier === this.bulkExpertCreateIdentifier
        )
        if (expertsToProcess.length > 0) {
          this.setState((prevState, props) => {
            return {
              bulkCreateFailedExperts: [
                ...prevState.bulkCreateFailedExperts,
                ...expertsToProcess
              ]
            }
          }, this.tryOpenExpertToProjectForm)
        }
      }
    )

    this.cancelledExpertsDispose = props.expertBulkCreateStore.observeCancelledExperts(
      experts => {
        const expertsToProcess = experts.filter(
          e => e.customIdentifier === this.bulkExpertCreateIdentifier
        )
        if (expertsToProcess.length > 0) {
          this.setState((prevState, props) => {
            return {
              bulkCreateFailedExperts: [
                ...prevState.bulkCreateFailedExperts,
                ...expertsToProcess
              ]
            }
          }, this.tryOpenExpertToProjectForm)
        }
      }
    )
  }

  componentWillUnmount() {
    this.processingExpertsDispose()
    this.completedExpertsDispose()
    this.errorExpertsDispose()
    this.cancelledExpertsDispose()
  }

  resetBulkCreateState() {
    this.setState((prevState, props) => {
      return {
        bulkCreateTrackingIds: [],
        bulkCreateCompletedExperts: [],
        bulkCreateFailedExperts: []
      }
    })
  }

  tryOpenExpertToProjectForm = () => {
    if (
      !this.addToProjectFormOpened &&
      this.state.bulkCreateTrackingIds.every(
        e =>
          this.state.bulkCreateCompletedExperts.some(
            k => k.bulkCreateTrackingId === e
          ) ||
          this.state.bulkCreateFailedExperts.some(
            k => k.bulkCreateTrackingId === e
          )
      ) &&
      this.state.bulkCreateCompletedExperts.length > 0
    ) {
      this.addToProjectFormOpened = true
      const duration = new Date().getTime() - this.bulkAddJobStartTime
      sendMetric(
        'bulkAddExpertsShowDlgTime',
        window.location.hostname,
        {},
        duration
      )
      sendMetric(
        'bulkAddExpertsShowDlgRate',
        window.location.hostname,
        {},
        duration / this.state.bulkCreateCompletedExperts.length
      )

      this.props.modalStore.show({
        type: 'EXPERT_TO_PROJECT_FORM',
        props: {
          experts: toJS(this.state.bulkCreateCompletedExperts.map(e => e.data)),
          resetPinned: () => {}
        }
      })
      this.props.expertBulkCreateStore.bulkAddJobStartTime = 0
      this.bulkAddJobStartTime = 0
    }
  }

  onSelect = id => this.props.expertSearchStore.selectExpert(id)

  onLink = async (e, id) => {
    if (e.target.className === 'name') return true // If user clicks on name just open link
    e.preventDefault()
    await this.props.expertSearchStore
      .fetchHighlight(id)
      .catch(() => console.error("Couldn't load highlights"))
    this.props.history.push('/expert-search/expert/' + id)
  }

  renderArrow = ({ isOpen }) => {
    if (isOpen) {
      return <ChevronIcon up />
    } else {
      return <ChevronIcon down />
    }
  }

  checkAll = val => {
    this.checkPinned(val)
    this.checkExperts(val)
  }

  checkPinned = val => {
    const count =
      SHERLOCK_LIMIT -
      this.props.expertSearchStore.experts.filter(e => e.checked).length
    this.props.expertSearchStore.pinnedExperts.forEach((e, i) =>
      i < count ? (e.checked = val) : null
    )
  }

  checkExperts = val => {
    const count =
      SHERLOCK_LIMIT -
      this.props.expertSearchStore.pinnedExperts.filter(e => e.checked).length
    this.props.expertSearchStore.experts
      .filter(e => e.experience?.company)
      .forEach((e, i) => (i < count ? (e.checked = val) : null))
  }

  addExpertsToProject = async () => {
    this.addToProjectFormOpened = false

    const pinned = this.props.expertSearchStore.pinnedExperts.map(e => ({
      ...e.data,
      listItemId: e.id,
      checked: e.checked
    }))
    const unPinned = this.props.expertSearchStore.experts
    const mergedExperts = union(unPinned, pinned)
    const experts = mergedExperts.filter(e => e.checked)

    if (!experts || experts.length < 1) {
      return this.props.growlStore.addNotification({
        title: 'Error!',
        message: 'Please select one or more experts.',
        type: 'error',
        autoClose: true
      })
    }

    this.bulkAddJobStartTime = new Date().getTime()

    const expertsToProcess = []

    // Existing experts
    expertsToProcess.push(
      ...experts
        .filter(e => e.id)
        .map(e => {
          return { id: e.id }
        })
    )

    // Non existing experts
    expertsToProcess.push(
      ...experts
        .filter(e => !e.id)
        .map(e => {
          const expert = JSON.parse(JSON.stringify(e))
          const item = JSON.parse(
            JSON.stringify(
              this.props.expertSearchStore.pinnedExperts.find(
                k => k.id === e.listItemId
              )
            )
          )

          // Special email mapping for AL
          expert.email = ''

          if (!expert.address) {
            expert.address = {}
          }

          if (!expert.address.emails) {
            expert.address.emails = []
          }

          if (Array.isArray(item.formatted?.emails)) {
            const allEmails = []
            for (let email of item.formatted?.emails) {
              if (typeof email === 'string' || email instanceof String) {
                allEmails.push(email)
              } else if (typeof email === 'object' && email.email) {
                allEmails.push(email.email)
              } else {
                // None of the above
              }
            }

            expert.address.emails = allEmails.map(e => ({ email: e.trim() }))
          } else if (
            typeof item.formatted?.emails === 'string' ||
            item.formatted?.emails instanceof String
          ) {
            const splitEmails = item.formatted.emails
              .split(',')
              .map(e => e.trim())
              .filter(e => e)
            expert.address.emails = splitEmails.map(e => ({ email: e }))
          }

          // ADD PHONE MAPPING
          if (!expert.address.phones) {
            expert.address.phones = []
          }

          if (Array.isArray(expert.formatted?.phones)) {
            const allPhones = []
            for (let phone of expert.formatted?.phones) {
              if (typeof phone === 'string' || phone instanceof String) {
                allPhones.push(phone)
              } else if (typeof phone === 'object' && phone.phoneNum) {
                //allPhones.push(phone.phoneNum)
              } else {
                // None of the above
              }
            }

            expert.address.phones = allPhones.map(e => ({ phoneNum: e.trim() }))
          } else if (
            typeof expert.formatted?.phones === 'string' ||
            expert.formatted?.phones instanceof String
          ) {
            const splitPhones = expert.formatted.phones
              .split(',')
              .map(e => e.trim())
              .filter(e => e)
            expert.address.phones = splitPhones.map(e => ({ phoneNum: e }))
          }

          expert.formatted = undefined

          return expert
        })
    )

    for (let expert of expertsToProcess) {
      expert.bulkCreateCustomIdentifier = this.bulkExpertCreateIdentifier

      let expertTypeMetricDimension = null
      if (expert.id) {
        if (Number(expert.id)) {
          expertTypeMetricDimension =
            AddToProcessingExpertTypeEnum.EXISTING_EXPERT
        } else {
          expertTypeMetricDimension = AddToProcessingExpertTypeEnum.PDL
        }
      } else {
        if (expert.pdlId) {
          expertTypeMetricDimension = AddToProcessingExpertTypeEnum.PDL
        } else {
          expertTypeMetricDimension =
            AddToProcessingExpertTypeEnum.NON_EXISTING_EXPERT
        }
      }

      sendAddToProcessingMetric(
        AddToProcessingActionEnum.EXPERT_SEARCH_ADD_TO_PROJECT,
        expertTypeMetricDimension
      )
    }

    sendInitializeProcessingMetric(
      InitializeProcessingActionEnum.EXPERT_SEARCH_ADD_TO_PROJECT
    )

    this.resetBulkCreateState()
    this.props.expertBulkCreateStore.bulkAddJobStartTime = this.bulkAddJobStartTime
    await this.props.expertBulkCreateStore.addExpertsToProcessingQueue(
      expertsToProcess,
      ExpertFieldOptions.EXPERT_ADD_TO_PROJECT_OPTIONS,
      null,
      true,
      false,
      true
    )
  }

  render() {
    const store = this.props.expertSearchStore
    const selectedLength = this.props.expertSearchStore.experts.filter(
      e => e.checked
    ).length

    const hasExpertsProcessing = !this.state.bulkCreateTrackingIds.every(
      e =>
        this.state.bulkCreateCompletedExperts.some(
          k => k.bulkCreateTrackingId === e
        ) ||
        this.state.bulkCreateFailedExperts.some(
          k => k.bulkCreateTrackingId === e
        )
    )

    return (
      <div>
        <div className="expert-search-header">
          <a
            href
            className={
              hasExpertsProcessing ? 'pull-left' : 'pull-left clickable'
            }
            onClick={() => {
              if (!hasExpertsProcessing) {
                this.addExpertsToProject()
              }
            }}
            disabled={hasExpertsProcessing}>
            <Add className="pull-right__icon" />
            <span className="pull-right__label m-l-sm m-r-sm">
              Add/Invite selected experts to project
            </span>
            {hasExpertsProcessing && (
              <span className="pull-right">
                <CircularProgress
                  style={{ color: '#429bdc', width: '1.3em', height: '1.3em' }}
                />
              </span>
            )}
          </a>
        </div>
        <div className="expert-search-header expert-search-header-blue">
          <div className="pull-right expert-search-header__tooltip-wrapper">
            <Tooltip
              tooltip={
                'Click to ' +
                (store.shouldShowPinnedExperts ? 'collapse' : 'expand') +
                ' the list of pinned experts'
              }>
              <div
                className="search-group__container"
                onClick={store.togglePinnedExperts}>
                {store.shouldShowPinnedExperts ? (
                  <ChevronIcon up />
                ) : (
                  <ChevronIcon down />
                )}
              </div>
            </Tooltip>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end'
            }}>
            <AthenaListPicker />
          </div>
        </div>
        <div className="expert-search-panel-results-container">
          {store.shouldShowPinnedExperts &&
            map(this.props.expertSearchStore.pinnedExperts, expert => {
              let expertId = undefined //expert.data ? expert.data.id : expert.id

              if (expert.data?.id || expert.data?.lp_id) {
                expertId = expert.data?.lp_id ?? expert.id
              } else {
                expertId = `list-${expert.listId}_item-${expert.id}`
              }

              return (
                <div
                  key={expertId}
                  className="expert-search-item-container position-relative">
                  <Link
                    onClick={e => {
                      this.onLink(e, expertId)
                    }}
                    to={'/expert/' + expertId}>
                    <ExpertSearchItem
                      expert={expert}
                      modalStore={this.props.modalStore}
                      onClick={() => this.onSelect(expertId)}
                      isSelected={
                        this.props.expertSearchStore.selected === expertId
                      }
                      updateStatus={toJS(
                        this.props.expertSearchStore.expertsUpdateStatus
                      ).find(id => id === expertId)}
                    />
                  </Link>
                  <div className="cbox-container">
                    <Checkbox
                      value={expert.checked}
                      onChange={val => (expert.checked = val)}
                      disabled={
                        this.props.expertSearchStore.pinnedExperts.filter(
                          e => e.checked
                        ).length +
                          this.props.expertSearchStore.experts.filter(
                            e => e.checked
                          ).length >
                          24 && !expert.checked
                      }
                    />
                  </div>
                </div>
              )
            })}
        </div>
        {this.props.expertSearchStore.didSearch && (
          <div className="expert-search-header expert-search-header-blue ">
            <div className="pull-right expert-search-header__sorting-tooltip-wrapper">
              <Tooltip tooltip="Sort by">
                <div>
                  <Dropdown
                    valueIsId
                    containerClassName="autocomplete__container--expert-results"
                    dropdownClassName="autocomplete__input--plain-text"
                    selectedItem={this.props.expertSearchStore.query.sortBy}
                    itemToString={item => item && item.label}
                    onChange={option =>
                      this.props.expertSearchStore.onChangeSortOrder(option.id)
                    }
                    options={toJS(this.props.expertSearchStore.sortOptions)}
                  />
                </div>
              </Tooltip>
            </div>
            <Checkbox
              value={this.areAllExpertsSelected}
              label=""
              onChange={val => this.checkExperts(val)}
            />
            <h4 className="expert-search-total-inline" style={{ fontSize: 13 }}>
              {selectedLength > 0 ? (
                `${selectedLength} of ${SHERLOCK_LIMIT} selected `
              ) : (
                <>
                  {store.experts.length === store.total ? '' : 'top '}
                  {store.experts.length} matches{' '}
                </>
              )}
              <Tooltip tooltip={'Total number of matches: ' + store.total}>
                <InfoIcon />
              </Tooltip>
            </h4>
          </div>
        )}

        <div className="expert-search-panel-results-container-big">
          {(this.props.expertSearchStore
            .expertBulkCreateInProgressPinIdentifiers.length !==
            this.props.expertSearchStore.expertBulkCreateCompletedPinIdentifiers
              .length ||
            this.props.expertSearchStore.saveItemRequestsRunning > 0) && (
            <LinearProgress />
          )}
          {this.showExperts()}
        </div>
        {store.loadMore ? (
          <Spinner inline />
        ) : (
          store.didSearch &&
          store.total !== store.experts.length + store.duplicateCount && (
            <div
              className="expert-search-item link"
              onClick={this.props.expertSearchStore.loadMoreExperts}>
              Load more...
            </div>
          )
        )}
      </div>
    )
  }
  showExperts() {
    const store = this.props.expertSearchStore

    if (store.loading) return <Spinner />
    if (!store.didSearch) {
      return (
        <div style={{ padding: 25, fontSize: '125%' }}>
          Please use some filters or enter a keyword to search for experts.
        </div>
      )
    }

    if (!store.experts.length) {
      return (
        <div style={{ padding: 25, fontSize: '125%' }}>
          No experts were found that match search criteria.
        </div>
      )
    }

    return map(this.props.expertSearchStore.experts, expert => {
      const expertId = expert.data ? expert.data.id : expert.id
      return (
        <div
          key={expertId}
          className="expert-search-item-container position-relative">
          <Link
            onClick={e => this.onLink(e, expert.id)}
            to={'/expert/' + expert.id}>
            <ExpertSearchItem
              expert={expert}
              modalStore={this.props.modalStore}
              onClick={() => this.onSelect(expert.id)}
              isSelected={this.props.expertSearchStore.selected === expert.id}
              updateStatus={toJS(
                this.props.expertSearchStore.expertsUpdateStatus
              ).find(id => id === expertId)}
            />
          </Link>
        </div>
      )
    })
  }
}

export default withRouter(
  inject(
    'expertSearchStore',
    'modalStore',
    'growlStore',
    'expertBulkCreateStore'
  )(observer(ExpertSearchResults))
)

const filter = createFilterOptions()

const AthenaListPicker = inject(['expertSearchStore'])(
  observer(({ expertSearchStore: store }) => {
    if (!store.massProcessingLists?.length) return null
    return (
      <>
        <Autocomplete
          value={
            store.lastListLoaded
              ? store.massProcessingList
              : { name: 'loading...' }
          }
          onChange={async (event, newValue) => {
            if (typeof newValue === 'string') {
              await store.setAthenaList({ id: 'new', name: newValue })
            } else {
              await store.setAthenaList(newValue)
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params)

            // Suggest the creation of a new value
            if (params.inputValue !== '') {
              filtered.push({
                id: 'new',
                label: `new: "${params.inputValue}"`,
                name: params.inputValue
              })
            }

            return filtered
          }}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          id="free-solo-with-text-demo"
          options={store.massProcessingLists}
          getOptionLabel={option => option.name}
          renderOption={option => option.label || option.name}
          style={{ width: 300 }}
          freeSolo
          renderInput={params => <TextField {...params} label="Athena List" />}
        />
        <Button
          style={{
            background: 'transparent',
            color: '#429bdc',
            marginLeft: '10px',
            height: '30px'
          }}
          onClick={() => {
            window.location.href = `/list/${store.massProcessingList.id}`
          }}>
          <ChevronRightIcon
            style={{
              width: '50px',
              height: '50px'
            }}
          />
        </Button>
      </>
    )
  })
)
